import { Component } from '@theme/component';

/**
 * [FROST BUDDY] Live school search for the "Collabs & Licensed" homepage finder
 * (sections/fb-collabs.liquid).
 *
 * The section inlines every NCAA team as JSON ({ t: title, n: nickname, c: conference,
 * u: url, l: logo url }); this filters it as the shopper types and renders logo rows.
 * Submitting with exactly one match goes straight to that school; otherwise the form
 * submits natively to the college collection's #find-your-team grid with ?school=,
 * which fb-team-search.js uses to prefill its search.
 *
 * The results panel (`dropdown`) floats over the page on desktop, so it closes on an
 * outside click / Escape. Its last row, "Don't see your school?", opens a <dialog>
 * that subscribes the shopper to Klaviyo (same client API as
 * blocks/fb-klaviyo-signup.liquid) with the school they typed as "Requested School".
 *
 * @typedef {{ t: string, n: string, c: string, u: string, l: string }} Team
 * @typedef {{ team: Team, score: number }} Match
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} status
 * @property {HTMLElement} dropdown
 * @property {HTMLUListElement} results
 * @property {HTMLAnchorElement} empty
 * @property {HTMLScriptElement} data
 * @property {HTMLButtonElement} [request]
 * @property {HTMLDialogElement} [dialog]
 * @property {HTMLFormElement} [requestForm]
 * @property {HTMLElement} [requestFields]
 * @property {HTMLInputElement} [requestSchool]
 * @property {HTMLInputElement} [requestEmail]
 * @property {HTMLButtonElement} [requestButton]
 * @property {HTMLElement} [requestMessage]
 *
 * @extends {Component<Refs>}
 */
class FbCollabFinder extends Component {
  requiredRefs = ['input', 'status', 'dropdown', 'results', 'empty', 'data'];

  /** @type {{ team: Team, haystack: string, title: string }[]} */
  #index = [];

  /** @type {Match[]} */
  #matches = [];

  /** @type {number | undefined} */
  #debounce;

  /** The school value we last pre-filled into the request modal (vs. one the shopper typed). */
  #prefilledSchool = '';

  connectedCallback() {
    super.connectedCallback();
    this.#buildIndex();
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#buildIndex();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#debounce);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    this.removeEventListener('focusout', this.#onFocusOut);
  }

  /** Close the floating results when the shopper clicks anywhere else. */
  #onDocumentPointerDown = (/** @type {PointerEvent} */ event) => {
    if (this.refs.dialog?.open) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeDropdown();
  };

  /** ...or tabs out of the finder. */
  #onFocusOut = (/** @type {FocusEvent} */ event) => {
    if (this.refs.dialog?.open) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    if (next === null) return; // focus left the window / clicked non-focusable: pointerdown handles it
    this.#closeDropdown();
  };

  #closeDropdown() {
    this.refs.dropdown.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
  }

  /** @returns {HTMLElement[]} Keyboard-reachable rows in the panel, top to bottom. */
  #focusableRows() {
    return [...this.refs.dropdown.querySelectorAll('a, button')].filter(
      (el) => el instanceof HTMLElement && !el.hidden && el.offsetParent !== null
    );
  }

  #buildIndex() {
    /** @type {Team[]} */
    let teams = [];
    try {
      teams = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      teams = [];
    }

    this.#index = teams.map((team) => ({
      team,
      title: normalize(team.t),
      haystack: normalize(`${team.t} ${team.n} ${team.c}`),
    }));
  }

  get #maxResults() {
    return Number(this.dataset.maxResults) || 5;
  }

  onInput() {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#search(), 120);
  }

  onInputFocus() {
    // Re-open the panel when coming back to a filled-in search.
    if (this.refs.input.value.trim() && this.refs.dropdown.hidden) this.#search();
  }

  /** @param {KeyboardEvent} event */
  onInputKeydown(event) {
    if (event.key === 'ArrowDown') {
      if (this.refs.dropdown.hidden) return;
      const [first] = this.#focusableRows();
      if (!first) return;
      event.preventDefault();
      first.focus();
    } else if (event.key === 'Escape') {
      if (!this.refs.dropdown.hidden) {
        event.preventDefault();
        this.#closeDropdown();
      } else if (this.refs.input.value) {
        event.preventDefault();
        this.refs.input.value = '';
        this.#search();
      }
    }
  }

  /** @param {KeyboardEvent} event */
  onDropdownKeydown(event) {
    const rows = this.#focusableRows();
    const index = rows.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (index === -1) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      rows[Math.min(index + 1, rows.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) this.refs.input.focus();
      else rows[index - 1]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDropdown();
      this.refs.input.focus();
    }
  }

  openRequest() {
    const { dialog, requestSchool, requestEmail } = this.refs;
    if (!dialog || !requestSchool || !requestEmail) return;

    // Pre-fill with the latest search, unless the shopper already typed their own school.
    const typed = this.refs.input.value.trim();
    if (typed && (!requestSchool.value || requestSchool.value === this.#prefilledSchool)) {
      requestSchool.value = typed;
      this.#prefilledSchool = typed;
    }
    this.#closeDropdown();
    dialog.showModal();
    (requestSchool.value ? requestEmail : requestSchool).focus();
  }

  closeRequest() {
    this.refs.dialog?.close();
  }

  /**
   * Backdrop click: the real target is the <dialog> itself, not its contents. Use
   * composedPath()[0]: the Component event proxy rewrites `event.target` to the element
   * carrying on:click (the dialog), so `target` would match every click inside it.
   */
  onDialogClick(/** @type {MouseEvent} */ event) {
    if (event.composedPath()[0] === this.refs.dialog) this.closeRequest();
  }

  /** @param {SubmitEvent} event */
  async onRequestSubmit(event) {
    event.preventDefault();
    const { dialog, requestSchool, requestEmail, requestButton, requestFields } = this.refs;
    if (!dialog || !requestEmail || !requestButton) return;

    this.#requestMessage('', 'status');
    if (!requestEmail.checkValidity()) {
      requestEmail.reportValidity();
      return;
    }

    const companyId = dialog.dataset.companyId;
    const listId = dialog.dataset.listId;
    if (!companyId || !listId) {
      this.#requestMessage('Signup is not configured yet. Please contact us.', 'error');
      return;
    }

    const email = requestEmail.value.trim();
    const school = (requestSchool?.value || '').trim();
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, string>} */
    const properties = {
      'Shopify Submission Source': 'Homepage school finder',
      'Signup Referrer': document.referrer || '',
    };
    if (school) properties['Requested School'] = school;
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = params.get(key);
      if (value) properties[key] = value;
    }

    requestButton.disabled = true;
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: '2026-04-15',
            accept: 'application/vnd.api+json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Shopify - Homepage school request',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        }
      );

      if (!response.ok) {
        let detail = 'Something went wrong. Please try again.';
        try {
          const json = await response.json();
          detail = json?.errors?.[0]?.detail || detail;
        } catch {
          /* keep the generic message */
        }
        this.#requestMessage(detail, 'error');
        requestButton.disabled = false;
        return;
      }

      this.#trackRequest(email, school, listId, properties);
      if (requestFields) requestFields.hidden = true;
      this.#requestMessage(dialog.dataset.success || "You're on the list.", 'status');
    } catch {
      // The request never completed: almost always an ad/privacy blocker on a.klaviyo.com.
      this.#requestMessage(
        "Couldn't reach the signup server. An ad or privacy blocker may be blocking it. Try pausing it, or use a different browser.",
        'error'
      );
      requestButton.disabled = false;
    }
  }

  /**
   * @param {string} text
   * @param {'status' | 'error'} kind
   */
  #requestMessage(text, kind) {
    const message = this.refs.requestMessage;
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
    message.dataset.kind = kind;
    message.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    if (text) message.focus();
  }

  /**
   * Best-effort onsite identify + the same event the Klaviyo signup block fires.
   * @param {string} email
   * @param {string} school
   * @param {string} listId
   * @param {Record<string, string>} properties
   */
  #trackRequest(email, school, listId, properties) {
    try {
      const w = /** @type {any} */ (window);
      const event = [
        'track',
        'Signed Up via Form',
        { email, source: 'Shopify - Homepage school request', list_id: listId, requested_school: school },
      ];
      if (w.klaviyo && typeof w.klaviyo.identify === 'function') {
        w.klaviyo.identify({ email, ...properties });
        w.klaviyo.push?.(event);
      } else if (Array.isArray(w._learnq)) {
        w._learnq.push(['identify', { $email: email, ...properties }]);
        w._learnq.push(event);
      }
    } catch {
      /* onsite tracking is best-effort */
    }
  }

  /** @param {SubmitEvent} event */
  onSubmit(event) {
    clearTimeout(this.#debounce);
    this.#search();

    const query = this.refs.input.value.trim();
    if (!query) {
      // Nothing typed: don't bounce them off the homepage, just put the cursor in.
      event.preventDefault();
      this.refs.input.focus();
      return;
    }

    const [only] = this.#matches;
    if (this.#matches.length === 1 && only) {
      event.preventDefault();
      window.location.assign(only.team.u);
    }
    // Otherwise let the native GET submit land on the full grid, prefilled.
  }

  #search() {
    const query = normalize(this.refs.input.value);
    const tokens = query.split(' ').filter(Boolean);

    if (!tokens.length) {
      this.#matches = [];
      this.#render('');
      return;
    }

    /** @type {Match[]} */
    const matches = [];
    for (const entry of this.#index) {
      if (!tokens.every((token) => entry.haystack.includes(token))) continue;

      // Rank: title starts with the query > a title word starts with it > anywhere.
      let score = 2;
      if (entry.title.startsWith(query)) score = 0;
      else if (` ${entry.title}`.includes(` ${tokens[0]}`)) score = 1;
      matches.push({ team: entry.team, score });
    }

    matches.sort((a, b) => a.score - b.score || a.team.t.localeCompare(b.team.t));
    this.#matches = matches;
    this.#render(query);
  }

  /** @param {string} query */
  #render(query) {
    const { dropdown, results, empty, status } = this.refs;
    results.replaceChildren();

    if (!query) {
      dropdown.hidden = true;
      results.hidden = true;
      empty.hidden = true;
      status.textContent = '';
      this.refs.input.setAttribute('aria-expanded', 'false');
      return;
    }

    const shown = this.#matches.slice(0, this.#maxResults);
    for (const { team } of shown) {
      results.append(this.#renderResult(team, query));
    }

    results.hidden = shown.length === 0;
    empty.hidden = shown.length > 0;
    dropdown.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');

    const count = this.#matches.length;
    const message =
      count === 0
        ? this.dataset.statusNone
        : count === 1
          ? this.dataset.statusOne
          : this.dataset.statusOther?.replace('[count]', String(count));
    status.textContent = message || '';
  }

  /**
   * @param {Team} team
   * @param {string} query
   */
  #renderResult(team, query) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.className = 'fb-collabs__finder-result';
    link.href = team.u;

    if (team.l) {
      const logo = document.createElement('img');
      logo.className = 'fb-collabs__finder-result-logo';
      logo.src = team.l;
      logo.alt = '';
      logo.width = 40;
      logo.height = 40;
      link.append(logo);
    }

    const name = document.createElement('span');
    name.className = 'fb-collabs__finder-result-name';
    name.append(...highlight(team.t, query));
    link.append(name);

    if (team.c) {
      const meta = document.createElement('span');
      meta.className = 'fb-collabs__finder-result-meta';
      meta.textContent = team.c;
      link.append(meta);
    }

    const arrow = document.createElement('span');
    arrow.className = 'fb-collabs__finder-result-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    link.append(arrow);

    item.append(link);
    return item;
  }
}

/**
 * Lowercase, strip accents/punctuation, collapse whitespace. "&" is kept as a word
 * character so "a&m" stays one token instead of matching every "a" + "m" school.
 * @param {string} value
 */
function normalize(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}

/**
 * Wraps the first case-insensitive occurrence of the query in <mark> (text nodes only).
 * @param {string} text
 * @param {string} query
 * @returns {(string | HTMLElement)[]}
 */
function highlight(text, query) {
  const at = text.toLowerCase().indexOf(query);
  if (!query || at === -1) return [text];

  const mark = document.createElement('mark');
  mark.textContent = text.slice(at, at + query.length);
  return [text.slice(0, at), mark, text.slice(at + query.length)];
}

if (!customElements.get('fb-collab-finder')) {
  customElements.define('fb-collab-finder', FbCollabFinder);
}
